import { useState } from 'react';
import { NavLink, Outlet, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, MoreHorizontal, X } from 'lucide-react';
import { useActiveTournamentMaybe } from '../state/TournamentContext';
import { useActiveRole } from '../state/RoleContext';
import { useAuth } from '../state/AuthContext';
import { paths, tournamentSlug } from '../lib/paths';
import { useStore } from '../hooks/useStore';
import { useOverlayScrollLock } from '../hooks/useOverlayScrollLock';
import { ScorekeeperIcon } from './ScorekeeperLogo';

interface PublicTab {
  to: string;
  label: string;
  end?: boolean;
  /** Hide on mobile bottom-bar; reachable via "More". */
  secondary?: boolean;
}

/**
 * Public event chrome — fan-facing, mobile-first. Used for `/events/:slug/...`.
 *
 * - Sticky compact header (event name + status + back-to-events).
 * - Web: horizontal tab strip below the hero.
 * - Mobile: bottom-tab nav (Stands · Schedule · My Team · More) — primary tabs
 *   always thumb-reachable. "More" opens a sheet with secondary tabs.
 *
 * No persona switcher here — the public site is fan-default. Admins drop into
 * the workspace via the admin shell.
 */
export function PublicShell() {
  const ctx = useActiveTournamentMaybe();
  const location = useLocation();
  const { role } = useActiveRole();
  const { canAdmin } = useAuth();
  const logoAssetId = useStore((db) => db.league.branding.logoAssetId);
  const [moreOpen, setMoreOpen] = useState(false);
  useOverlayScrollLock(moreOpen);

  const tournament = ctx?.tournament;
  const slug = tournament?.slug;

  const tabs: PublicTab[] = slug
    ? [
        { to: paths.event(slug), label: 'Stands', end: true },
        { to: paths.eventTab(slug, 'schedule'), label: 'Schedule' },
        { to: paths.eventTab(slug, 'stats'), label: 'Stats' },
      ]
    : [];

  // Personalized tabs — added based on role. "My Team" is the primary lens for
  // captains/players, so it earns a bottom-bar slot when applicable.
  if (slug) {
    if (role === 'captain' || role === 'admin') {
      tabs.push({ to: paths.eventTab(slug, 'my-team'), label: 'My Team' });
    }
    if (role === 'player' || role === 'admin') {
      tabs.push({ to: paths.eventTab(slug, 'my-stats'), label: 'My Stats', secondary: true });
    }
    if (role === 'scorekeeper' || role === 'referee' || role === 'admin') {
      tabs.push({ to: paths.eventTab(slug, 'my-games'), label: 'My Games', secondary: true });
    }
    tabs.push(
      { to: paths.eventTab(slug, 'locker-room'), label: 'Locker Room', secondary: true },
      { to: paths.eventTab(slug, 'front-desk'), label: 'Front Desk', secondary: true },
    );
  }

  const primaryTabs = tabs.filter((t) => !t.secondary).slice(0, 3);
  const secondaryTabs = tabs.filter((t) => t.secondary || !primaryTabs.includes(t));

  return (
    <div className="public-shell">
      <header className="public-shell__hero">
        <div className="public-shell__hero-inner">
          <div className="public-shell__hero-top">
            <Link to={paths.events} className="btn btn--ghost btn--sm" aria-label="All events">
              <ArrowLeft size={14} /> Events
            </Link>
            <div className="public-shell__brand">
              {logoAssetId ? (
                <img
                  src={`/api/public-asset?id=${encodeURIComponent(logoAssetId)}`}
                  alt=""
                  style={{ width: 22, height: 22, objectFit: 'contain', borderRadius: 6 }}
                />
              ) : (
                <ScorekeeperIcon className="public-shell__brand-icon" />
              )}
              <div className="public-shell__brand-text">
                <div className="public-shell__title">{tournament?.name ?? 'Live Event'}</div>
                <div className="public-shell__sub">
                  {tournament
                    ? `${tournament.status.replace('_', ' ')} · ${new Date(tournament.startsAt).toLocaleDateString()} – ${new Date(tournament.endsAt).toLocaleDateString()}`
                    : 'Real-time scores, schedule, and team rooms.'}
                </div>
              </div>
            </div>
            {canAdmin && tournament && (
              <Link
                to={paths.adminCompetition(tournament.format, tournamentSlug(tournament))}
                className="btn btn--ghost btn--sm public-shell__admin-link"
              >
                Admin
              </Link>
            )}
          </div>

          {tabs.length > 0 && (
            <nav className="public-shell__tabs" aria-label="Event sections">
              {tabs.map((t) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  end={t.end}
                  className={({ isActive }) =>
                    `public-shell__tab ${isActive || (t.end && location.pathname === t.to) ? 'is-active' : ''}`
                  }
                >
                  {t.label}
                </NavLink>
              ))}
            </nav>
          )}
        </div>
      </header>

      <main className="public-shell__main">
        <div className="page">
          <Outlet />
        </div>
      </main>

      {tabs.length > 0 && (
        <nav className="bottom-nav" aria-label="Event navigation">
          <ul className="bottom-nav__list">
            {primaryTabs.map((t) => (
              <li key={t.to} style={{ flex: 1 }}>
                <NavLink
                  to={t.to}
                  end={t.end}
                  className={({ isActive }) => `bottom-nav__item ${isActive ? 'is-active' : ''}`}
                >
                  <span>{t.label}</span>
                </NavLink>
              </li>
            ))}
            {secondaryTabs.length > 0 && (
              <li style={{ flex: 1 }}>
                <button
                  type="button"
                  className={`bottom-nav__item ${moreOpen ? 'is-active' : ''}`}
                  onClick={() => setMoreOpen(true)}
                  aria-expanded={moreOpen}
                >
                  <MoreHorizontal size={20} />
                  <span>More</span>
                </button>
              </li>
            )}
          </ul>
        </nav>
      )}

      {moreOpen && (
        <div
          className="sheet"
          role="dialog"
          aria-modal="true"
          aria-label="More sections"
          onClick={() => setMoreOpen(false)}
        >
          <div className="sheet__panel overlay-panel" onClick={(e) => e.stopPropagation()}>
            <div className="sheet__head overlay-head">
              <span style={{ fontWeight: 600 }}>More</span>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                aria-label="Close"
                onClick={() => setMoreOpen(false)}
              >
                <X size={16} />
              </button>
            </div>
            <div className="sheet__body overlay-body touch-scroll">
              {secondaryTabs.map((t) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  onClick={() => setMoreOpen(false)}
                  className={({ isActive }) => `sheet__item ${isActive ? 'is-active' : ''}`}
                >
                  {t.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
